// src/components/GeneratedPurchaseOrder/PurchaseOrder.jsx
import React, { Fragment, useMemo } from "react";
import { Grid, Typography } from "@mui/material";
import Content from "../Content";
import PricingTable from "../../../PricingTable";
import ArtWorkDetails from "../ArtWorkDetails";
import { selectDecorationsForPurchaseOrder, resolveRenderedDecorationArtwork, getArtworkRenderPreviewId, getArtworkDecorationId, getDecorationId, getPreparedRenderPreviewUrl, getRenderPreviewSourceType } from "../proofPreviewHelpers";
import styles from "./index.module.css";

const safeParseJSON = (val, fallback) => {
  if (val == null) return fallback;
  if (typeof val !== "string") return val;
  try {
    const parsed = JSON.parse(val);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const num = (v) => Number.parseFloat(v) || 0;

// Put with other small helpers (top of file)
const humanizeDecorator = (details = {}, fallbacks = {}) => {
  const methodName = details?.methodName;
  // If it's already a nice label, use it
  if (methodName && !/^supplier\|\$/.test(methodName) && !/^thirdparty\|/.test(methodName)) {
    return methodName;
  }

  const source = details?.source || "";
  if (!source) return methodName || "";

  // Basic humanizer for old data where methodName==source
  if (source === "elite") return "Elite Decorations (In-House)";
  if (source === "supplier-custom") return "Custom Request (Supplier)";
  if (source === "supplier|$UNI") return "Unimprinted (Supplier)";

  if (source.startsWith("thirdparty|")) {
    const [, idStr] = source.split("|");
    // If you want, you can pass EXTERNAL_DECORATORS via fallbacks and resolve here.
    return "3rd-Party Decorator";
  }

  if (source.startsWith("supplier|$")) {
    // We don't have imprintMethods in this file, so best-effort:
    return "Supplier Decoration";
  }

  return methodName || source;
};

export const resolvePurchaseOrderImprintType = ({
  artwork = {},
  decoratorDetails = {},
  fallbackImprintType = "",
} = {}) => {
  const artworkImprintType = String(
    artwork?.ImprintType ?? artwork?.imprintType ?? ""
  ).trim();
  const isRoutingToken =
    /^supplier\|\$/i.test(artworkImprintType) ||
    /^thirdparty\|/i.test(artworkImprintType);

  return (
    (!isRoutingToken && artworkImprintType) ||
    humanizeDecorator(decoratorDetails) ||
    String(fallbackImprintType ?? "").trim() ||
    artworkImprintType
  );
};


const PurchaseOrder = ({ productData, currency, type, supplierInfo }) => {
  const {
    Name,
    pricings = [],
    ProductId,
    ArtWorks = [],
    artworks = [],
    charges = [], // used for Same/Combined + service-only
  } = productData;

  const firstNonEmptyArray = (...arrays) => arrays.find((value) => Array.isArray(value) && value.length > 0) || [];

  const decorations = useMemo(
    () =>
      selectDecorationsForPurchaseOrder({
        purchaseOrderType: type,
        supplierInfo,
        item: productData,
      }).map((d) => {
        const details = safeParseJSON(d?.DecoratorDetails ?? d?.decoratorDetails, {});
        const charge = Array.isArray(d?.Charge ?? d?.charges)
          ? (d.Charge ?? d.charges)
          : safeParseJSON(d?.Charge ?? d?.charges, []);
        const source = String(details?.source ?? details?.Source ?? d?.source ?? '').trim().toLowerCase();
        const supplier = d?.decorationSupplier || {};
        return {
          ...d,
          DecoratorDetails: { ...(details || {}), source },
          Charge: Array.isArray(charge) ? charge : [],
          isSupplierCustom: d?.isSupplierCustom || source === 'supplier-custom',
          decorationSupplier: supplier,
        };
      }),
    [productData, supplierInfo, type]
  );

  const totalQuantity = pricings.reduce((sum, p) => sum + (Number(p.Quantity) || 0), 0);

  // Mode flags:
  const isNo = type === "No_decoration";
  const isSame = type === "Same_supplier_decoration";
  const isExternal = type === "Different_supplier_decoration";
  const isCombined = type === "Combined_supplier_and_external_decoration";
  const isService = type === "Other_service_art";
  const isDecoratorPo = Boolean(supplierInfo?.decoratorId);
  const supplierNote = !isDecoratorPo ? productData?.shipping?.supplierNotes : '';

  // 1) Build the main product‐pricing table (skip for service-only)
  //    👉 uses NetCost for unit price and row amount
  const priceDetails = () => {
    const rows = (pricings || []).map((p) => {
      const unit = num(p.NetCost);
      const qty = num(p.Quantity);
      const sku = (p?.SKU ?? productData?.SKU ?? "").trim();
      return {
        id: ProductId,
        sku,
        item: `${p.Size || ""}${p.Size && p.Color ? " – " : ""}${p.Color || ""}`,
        price: p.NetCost,               // unit cost we pay
        quantity: p.Quantity,
        amount: unit * qty,             // per-row cost
      };
    });

    if (isExternal) {
      rows.push({
        id: "",
        item: "Total Units",
        price: "",
        quantity: totalQuantity,
        amount: "",
      });
    } else {
      const total = rows.reduce((sum, r) => sum + num(r.amount), 0);
      rows.push(
        { id: "", item: "Total Units", price: "", quantity: totalQuantity, amount: "" },
        //{ id: "", item: "Total", price: "", quantity: "", amount: `${total.toFixed(2)} ${currency || ""}` }
      );
    }

    return rows;
  };

  // 2) Split decorations into inline vs external buckets
  const inlineDec = decorations.filter((d) => d.isSupplierCustom || !d?.DecoratorDetails?.decoratorId);

  // for external/combined POs, match the PO’s decorator id (BE puts it on supplierInfo)
  const externalDec =
    isExternal || isCombined
      ? decorations.filter((d) => !d.isSupplierCustom)
      : [];

  const decorationArtwork = (dec, idx) => {
    const sourceIndex = dec?.__scopeIndex ?? idx;
    return resolveRenderedDecorationArtwork(dec, sourceIndex,
      ArtWorks.length ? ArtWorks : artworks);
  };
  // In the headless PDF only, a customer-approved JPG may replace an older
  // original PDF in the display object. The server still requires the original
  // PDF marker. Render that genuine source artwork separately when needed.
  const originalArtworkRequiredForPdf = (dec, idx) => {
    const sources = productData?.__originalArtworkSources || [];
    const decorationId = getDecorationId(dec);
    const original = sources.find((art) =>
      decorationId && getArtworkDecorationId(art) === decorationId
    );
    if (!original || !getPreparedRenderPreviewUrl(original)) return null;
    const displayed = decorationArtwork(dec, idx);
    if (getArtworkRenderPreviewId(displayed) === getArtworkRenderPreviewId(original) &&
        ["pdf", "eps"].includes(getRenderPreviewSourceType(displayed))) return null;
    return original;
  };
  const decorationSupplierName = (dec) => dec.isSupplierCustom
    ? dec?.decorationSupplier?.name
      || productData?.SupplierName
      || productData?.supplierName
      || productData?.supplier?.Name
      || supplierInfo?.supplierName
    : null;

  // helper: map a charge to net-based row with fallbacks
  const mapChargeToNetRow = (dec, c) => {
      const unit =
        c?.NetCost ??
        c?.unitCost ??
        c?.UnitCost ??
        c?.RetailPrice ??
       c?.Retail ??
        0;
      const qty = c?.Quantity ?? c?.quantity ?? 1;
      const explicitRaw = c?.totalNet ?? c?.TotalNet ?? c?.total ?? c?.Total;
      const explicitNum = explicitRaw != null ? num(explicitRaw) : NaN;
      // Prefer explicit only if it's a positive number; otherwise fall back
      const computed = num(unit) * num(qty);
      const amount = Number.isFinite(explicitNum) && explicitNum > 0 ? explicitNum : computed;
    return {
      decoration: dec?.Location,
      item: c?.Name ?? c?.title ?? "Charge",
      price: unit,
      quantity: qty,
      amount
    };
  };

  return (
    <Grid container spacing={3} className={styles.presentationCardContainer}>
      {/* ――― Header ――― */}
      <Grid item xs={12} style={{ padding: "12px" }}>
        <Content title={Name} type="Text" />
      </Grid>

      {/* ――― Service-only: just render the service charges table (net-based) ――― */}
      {isService && (
        <Grid item xs={12}>
          <PricingTable
            priceDetails={(charges || []).map((c) => {
              const unit =
                c?.NetCost ??
                c?.unitCost ??
                c?.UnitCost ??
                c?.RetailPrice ??
                c?.Retail ??
                0;
              const qty = c?.quantity ?? c?.Quantity ?? 1;
              const explicit =
                c?.totalNet ??
                c?.TotalNet ??
                c?.total ??
                c?.Total ??
                (num(unit) * num(qty));
              return {
                item: c.title || c.Name || "Service",
                price: unit,
                quantity: qty,
                amount: explicit,
              };
            })}
            isPo
            isArtWork
            supplierNote={supplierNote}
          />
        </Grid>
      )}

      {/* ――― Main Pricing for non-service POs ――― */}
      {!isService && (
        <Grid item xs={12}>
          <PricingTable
            priceDetails={priceDetails()}
            isPo
            NetCost
            isDecoration={isExternal}
            supplierNote={supplierNote}
          />
        </Grid>
      )}

      {/* ―――  No_decoration: nothing extra ――― */}
      {isNo && null}

      {/* ―――  Same_supplier_decoration: show inline supplier charges/details (net-based) ――― */}
      {!isService &&
        isSame &&
        inlineDec.map((dec, idx) => (
          <Fragment key={idx}>
            <Grid item xs={12}>
              <Typography className={styles.artWorkCostsTitle}>
                Decoration Costs
              </Typography>
              <PricingTable
                priceDetails={(dec.Charge || []).map((c) => mapChargeToNetRow(dec, c))}
                isPo
                isArtWork
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className={styles.artWorkTitle}>
                Decoration Details
              </Typography>
              <ArtWorkDetails
                artwork={(() => {
                  const artwork = decorationArtwork(dec, idx);
                  return {
                    ...artwork,
                    // The artwork selection is the customer-facing imprint type.
                    // Supplier method/source describes routing and is only a fallback
                    // for legacy records that do not have an artwork imprint type.
                    ImprintType: resolvePurchaseOrderImprintType({
                      artwork,
                      decoratorDetails: dec.DecoratorDetails,
                      fallbackImprintType: ArtWorks[idx]?.ImprintType ?? "",
                    }),
                  };
                })()}
                type={type}
                location={dec.Location}
                decorationSupplierName={decorationSupplierName(dec)}
              />
            </Grid>
          </Fragment>
        ))}

      {/* ―――  Different_supplier_decoration: external numbering (net-based) ――― */}
      {!isService &&
        isExternal &&
        externalDec.map((dec, idx) => {
          const bullet = idx + 1;
          const costRows = (dec.Charge || []).map((c) => mapChargeToNetRow(dec, c));
          return (
            <Fragment key={idx}>
              <Grid item xs={12}>
                <div className={styles.numberedSection}>
                  <Typography variant="h6">
                    <span className={styles.bullet}>{bullet}</span> Artwork Costs
                  </Typography>
                </div>
                <PricingTable priceDetails={costRows} isPo isArtWork />
              </Grid>
              <Grid item xs={12}>
                <div className={styles.numberedSection}>
                  <Typography variant="h6">
                    <span className={styles.bullet}>{bullet}</span> Artwork Details
                  </Typography>
                </div>
                <ArtWorkDetails
                  artwork={decorationArtwork(dec, idx)}
                  type={type}
                  location={dec.Location}
                />
                {originalArtworkRequiredForPdf(dec, idx) && (
                  <>
                    <Typography variant="caption">Original Artwork</Typography>
                    <ArtWorkDetails
                      artwork={originalArtworkRequiredForPdf(dec, idx)}
                      type={type}
                      location={dec.Location}
                    />
                  </>
                )}
              </Grid>
            </Fragment>
          );
        })}

      {/* ―――  Combined_supplier_and_external_decoration: both (net-based) ――― */}
      {!isService && isCombined && (
        <>
          {/* Supplier inline charges */}
          <Grid item xs={12}>
            <Typography className={styles.artWorkCostsTitle}>
              Supplier Decoration Costs
            </Typography>
            <PricingTable
              priceDetails={inlineDec.flatMap((dec) =>
                (dec.Charge || []).map((c) => mapChargeToNetRow(dec, c))
              )}
              isPo
              isArtWork
            />
          </Grid>

          {/* External (numbered) */}
          {externalDec.map((dec, idx) => {
            const bullet = idx + 1;
            const costRows = (dec.Charge || []).map((c) => mapChargeToNetRow(dec, c));
            return (
              <Fragment key={idx}>
                <Grid item xs={12}>
                  <div className={styles.numberedSection}>
                    <Typography variant="h6">
                      <span className={styles.bullet}>{bullet}</span> External Artwork Costs
                    </Typography>
                  </div>
                  <PricingTable priceDetails={costRows} isPo isArtWork />
                </Grid>
                <Grid item xs={12}>
                  <div className={styles.numberedSection}>
                    <Typography variant="h6">
                      <span className={styles.bullet}>{bullet}</span> External Artwork Details
                    </Typography>
                  </div>
                  <ArtWorkDetails
                    artwork={decorationArtwork(dec, idx)}
                    type={type}
                    location={dec.Location}
                  />
                </Grid>
              </Fragment>
            );
          })}
        </>
      )}
    </Grid>
  );
};

export default PurchaseOrder;
